import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import Layout from "../component/layout";
import { getItem } from "../../../utils/localStorage";
import { getFacilityUnits, getLeaseInvoice, getFacilityInvoices } from "../../../utils/urls";
import { makeRequest2 } from "../../../utils/makeRequest";
import { toastify } from "../../../utils/toast";
import { Dialog } from 'primereact/dialog';
import { Tag } from 'primereact/tag';

function Dashboard() {
  const navigate = useNavigate();
  const [occupiedUnits, setOccupiedUnits] = useState(0);
  const [notOccupiedUnits, setUnoccupiedUnits] = useState(0);
  const [occupiedByHomeowner, setOccupiedByHomeowner] = useState(0);
  const [occupiedUnitData, setOccupiedUnitData] = useState([]);
  const [unoccupiedUnitData, setUnoccupiedUnitData] = useState([]);
  const [occupiedByHomeownerData, setOccupiedByHomeownerData] = useState([]);
  const [homeOwnerUnitsWithNoTenant, setHomeOwnerUnitsWithNoTenant] = useState(0);
  const [homeOwnerUnitsWithNoTenantData, setHomeOwnerUnitsWithNoTenantData] = useState([]);
  const [totalUnits, setTotalUnits] = useState(0);
  const [userName, setFullname] = useState("");
  const [userRole, setRole] = useState("");
  const [unitListModalOpen, setUnitListModalOpen] = useState(false);
  const [unitList, setUnitList] = useState([]);
  const [modalTitle, setModalTitle] = useState("Units");
  const [filterMonth, setFilterMonth] = useState("");
  const [filterStartDate, setFilterStartDate] = useState("");
  const [filterEndDate, setFilterEndDate] = useState("");
  const [selectedYear, setSelectedYear] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('');
  const [leaseInvoices, setLeaseInvoices] = useState([]);
  const [levyInvoices, setLevyInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const toast = useRef(null);
  // Loading facility modules
  const [facility, setFacility] = useState(null);

  useEffect(() => {
    const getFacilityData = async () => {
      try {
        const storedFacility = await getItem("selectedFacility");
        if (storedFacility) {
          const facilityData = typeof storedFacility === "string"
            ? JSON.parse(storedFacility)
            : storedFacility;
          setFacility(facilityData);
        }
      } catch (err) {
        console.error("Error retrieving facility data:", err);
      }
    };
    getFacilityData();
  }, []);

  const modules = facility?.modules || {};
  // End Load facility modules
  const getCurrentUser = async () => {
    try {
      const currentUserValue = await getItem("APPUSER");
      console.log("CURRENT user", currentUserValue)
      console.log("RAW APPUSER:", currentUserValue, typeof currentUserValue);
      let currentUser;
      if (typeof currentUserValue === "string") {
        try {
          currentUser = JSON.parse(currentUserValue);
        } catch (err) {
          console.error("Error parsing APPUSER:", err);
        }
      } else {
        currentUser = currentUserValue;
      }
      if (currentUser?.user) {
        const fullName = currentUser.user.fullName;
        const role = currentUser.user.role;
        setFullname(fullName);
        setRole(role);
        console.log("ROLE CHECK:", JSON.stringify(userRole), typeof userRole);
        console.log("ROLEEEE", role)
      } else {
        console.error("Invalid user structure:", currentUser);
      }
    } catch (err) {
      console.error(err.message);
    }
  };

  const fetchUnitsCount = async () => {
    try {
      const facilityId = await getItem("selectedFacilityId");
      const response = await makeRequest2(`${getFacilityUnits}/${facilityId}`, "GET", {});
      if (response.success) {
        console.log("Units API response.data:", response.data);

        const data = response.data || {};

        const units = Array.isArray(data.units) ? data.units : [];
        const occupiedUnits = Array.isArray(data.occupiedUnits) ? data.occupiedUnits : [];
        const notOccupiedUnits = Array.isArray(data.notOccupiedUnits) ? data.notOccupiedUnits : [];
        const occupiedByHomeowner = Array.isArray(data.occupiedByHomeowner) ? data.occupiedByHomeowner : [];
        const homeOwnerUnitsWithNoTenant = Array.isArray(data.homeOwnerUnitsWithNoTenant)
          ? data.homeOwnerUnitsWithNoTenant
          : [];

        const totalUnits = units.length;

        setTotalUnits(totalUnits);
        setOccupiedUnits(occupiedUnits.length);
        setUnoccupiedUnits(notOccupiedUnits.length);
        setOccupiedByHomeowner(occupiedByHomeowner.length);

        setOccupiedUnitData(occupiedUnits);
        setUnoccupiedUnitData(notOccupiedUnits);
        setOccupiedByHomeownerData(occupiedByHomeowner);

        setHomeOwnerUnitsWithNoTenant(homeOwnerUnitsWithNoTenant.length);
        setHomeOwnerUnitsWithNoTenantData(homeOwnerUnitsWithNoTenant);
      }
      else {
        throw new Error("Failed to fetch units count");
      }
    } catch (err) {
      console.error(err.message);
      toastify(err.message, "error");
    }
  };

  const handleViewUnits = (type) => {
    if (type === "occupied") {
      setModalTitle("Occupied Units (Tenants Resides Here)");
      setUnitList(occupiedUnitData);
    } else if (type === "occupiedByHomeowner") {
      setModalTitle("Occupied By HomeOwner (HomeOwner Resides Here)");
      setUnitList(occupiedByHomeownerData);
    } else if (type === "homeOwnerUnitsWithNoTenant") {
      setModalTitle("HomeOwner Units (Unit Does Not Have Tenant)");
      setUnitList(homeOwnerUnitsWithNoTenantData);
    } else {
      setModalTitle("Unoccupied Units (Unit Does Not Have Tenant)");
      setUnitList(unoccupiedUnitData);
    }
    setUnitListModalOpen(true);
  };

  const fetchLeaseInvoices = async () => {
    try {
      setLoading(true);
      const selectedFacilityId = await getItem("selectedFacilityId");
      const response = await makeRequest2(`${getLeaseInvoice}/${selectedFacilityId}`, "GET");

      if (response.success) {
        const invoiceData = Array.isArray(response.data) ? response.data :
          Array.isArray(response.data?.data) ? response.data.data : [];
        const transformedData = invoiceData.map(invoice => ({
          ...invoice,
          customerInfo: invoice.customerInfo || {
            fullName: 'No Tenant Assigned',
            firstName: '',
            lastName: ''
          }
        }));
        setLeaseInvoices(transformedData);
      } else {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: response.message || "Failed to fetch lease invoices"
        });
        setLeaseInvoices([]);
      }
    } catch (error) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: error.message || "Error loading lease invoices"
      });
      setLeaseInvoices([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchLevyInvoices = async () => {
    try {
      setLoading(true);
      const selectedFacilityId = await getItem("selectedFacilityId");
      const response = await makeRequest2(`${getFacilityInvoices}/${selectedFacilityId}`, "GET");

      if (response.success) {
        const invoices = Array.isArray(response.data?.data)
          ? response.data.data
          : [];

        const filteredInvoices = invoices
          .filter(invoice => invoice?.whatFor?.invoiceType === "Contract")
          .map(invoice => ({
            ...invoice,
            fullName: invoice.customerInfo?.fullName || ''
          }));

        setLevyInvoices(filteredInvoices);
      } else {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: response.error || "Failed to fetch invoices",
        });
      }
    } catch (error) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: error.message,
      });
    } finally {
      setLoading(false);
    }
  };

  const filterInvoicesByDate = (invoices) => {
    if (!Array.isArray(invoices)) {
      return [];
    }

    if (!filterMonth && !filterStartDate && !filterEndDate) {
      return invoices;
    }

    return invoices.filter(invoice => {
      // Parse the invoice's yearmonth or fallback to createdAt
      let invoiceDate;
      if (invoice.yearMonth) {
        const [year, month] = invoice.yearMonth.split('-');
        invoiceDate = new Date(parseInt(year), parseInt(month) - 1, 1);
      } else {
        invoiceDate = new Date(invoice.createdAt);
      }

      // Check month/year filter if selected
      if (filterMonth) {
        const invoiceMonth = invoiceDate.getMonth() + 1;
        const invoiceYear = invoiceDate.getFullYear();
        const [selectedYear, selectedMonth] = filterMonth.split('-');

        if (invoiceMonth !== parseInt(selectedMonth) ||
          invoiceYear !== parseInt(selectedYear)) {
          return false;
        }
      }

      // Check date range filter if both dates are selected
      if (filterStartDate && filterEndDate) {
        const startDate = new Date(filterStartDate);
        const endDate = new Date(filterEndDate);

        // For date range, we might want to check if the invoice month 
        // falls within the date range
        if (invoiceDate < startDate || invoiceDate > endDate) {
          return false;
        }
      }

      return true;
    });
  };

  const getYearOptions = () => {
    const thisYear = new Date().getFullYear();
    return Array.from({ length: 5 }, (_, i) => thisYear - 2 + i);
  };

  const getMonthOptions = () =>
    Array.from({ length: 12 }, (_, i) => ({
      value: String(i + 1).padStart(2, '0'),
      name: new Date(0, i).toLocaleString('default', { month: 'long' })
    }));

  const years = getYearOptions();
  const months = getMonthOptions();

  const handleMonthYearChange = (year, month) => {
    if (year && month) {
      setFilterMonth(`${year}-${month}`);
    } else {
      setFilterMonth('');
    }
  };

  const clearFilters = () => {
    setSelectedMonth('');
    setSelectedYear('');
    setFilterMonth('');
    setFilterStartDate('');
    setFilterEndDate('');
  };

  const COLORS = {
    primary: '#4f46e5',
    success: '#10b981',
    danger: '#ef4444',
    warning: '#f59e0b',
    info: '#06b6d4',
    secondary: '#6b7280'
  };

  const filteredLeaseInvoices = filterInvoicesByDate(leaseInvoices.filter(invoice => invoice.status !== 'Void'));
  const leaseData = {
    totalAmount: filteredLeaseInvoices.reduce(
      (sum, invoice) => sum + Number(invoice.totalAmount || 0),
      0
    ),
    totalPaid: filteredLeaseInvoices.reduce(
      (sum, invoice) => sum + Number(invoice.amountPaid || 0),
      0
    ),
    totalBalance: filteredLeaseInvoices.reduce((sum, invoice) => {
      const total = Number(invoice.totalAmount || 0);
      const paid = Number(invoice.amountPaid || 0);
      return sum + Math.max(total - paid, 0);
    }, 0),

    paidCount: filteredLeaseInvoices.filter(invoice => invoice.status === 'Paid').length,
    unpaidCount: filteredLeaseInvoices.filter(invoice => invoice.status === 'Unpaid').length,
    totalCount: filteredLeaseInvoices.length
  };

  const filteredLevyInvoices = filterInvoicesByDate(levyInvoices.filter(invoice => invoice.status !== 'Void'));
  const levyData = {
    totalAmount: filteredLevyInvoices.reduce((sum, invoice) => sum + (invoice.totalAmount || 0), 0),
    totalPaid: filteredLevyInvoices.reduce((sum, invoice) => sum + (invoice.amountPaid || 0), 0),
    totalBalance: filteredLevyInvoices.reduce((sum, invoice) => sum + ((invoice.totalAmount || 0) - (invoice.amountPaid || 0)), 0),
    paidCount: filteredLevyInvoices.filter(invoice => invoice.status === 'Paid').length,
    unpaidCount: filteredLevyInvoices.filter(invoice => invoice.status === 'Unpaid').length,
    totalCount: filteredLevyInvoices.length
  };

  const leaseChartData = [
    { name: 'Total Billed', value: leaseData.totalCount, amount: leaseData.totalAmount },
    { name: 'Collected', value: leaseData.paidCount, amount: leaseData.totalPaid },
    { name: 'Outstanding', value: leaseData.unpaidCount, amount: leaseData.totalBalance }
  ];

  const levyChartData = [
    { name: 'Total Billed', value: levyData.totalCount, amount: levyData.totalAmount },
    { name: 'Collected', value: levyData.paidCount, amount: levyData.totalPaid },
    { name: 'Outstanding', value: levyData.unpaidCount, amount: levyData.totalBalance }
  ];

  const formatCurrency = (amount) => `KES ${amount.toLocaleString()}`;

  useEffect(() => {
    fetchUnitsCount();
    getCurrentUser();
  }, []);

  useEffect(() => {
    fetchLeaseInvoices();
    fetchLevyInvoices();
  }, []);

  const cardStyle = {
    border: 'none',
    borderRadius: '16px',
    boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.05), 0 8px 10px -6px rgba(0, 0, 0, 0.01)',
    backgroundColor: '#ffffff',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
    overflow: 'hidden'
  };

  const clickableCardStyle = {
    ...cardStyle,
    cursor: 'pointer'
  };

  const iconCircleStyle = (bgColor) => ({
    width: '52px',
    height: '52px',
    borderRadius: '14px',
    backgroundColor: bgColor,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '24px'
  });

  const sectionHeaderStyle = {
    background: 'transparent',
    borderBottom: '1px solid #f1f5f9',
    padding: '1.25rem 1.5rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
  };


  if (userRole === 'reception') {
    return (
      <Layout>
        <div className="d-flex align-items-center justify-content-center" style={{ minHeight: '60vh' }}>
          <div className="text-center p-5" style={{ maxWidth: '480px' }}>
            <div
              className="d-flex align-items-center justify-content-center mx-auto mb-4"
              style={{
                width: '72px',
                height: '72px',
                borderRadius: '20px',
                backgroundColor: '#eef2ff'
              }}
            >
              <i className="ti ti-ban text-danger" style={{ fontSize: '50px' }}></i>
            </div>
            <h4 className="fw-bold mb-2" style={{ color: '#0f172a' }}>
              Dashboard Not Available
            </h4>
            <p className="text-muted mb-4">
              Your account only has access to the Visitor Management System. Please use the sidebar to navigate there.
            </p>
          </div>
        </div>
      </Layout>
    );
  }


  return (
    <Layout>
      <div className="row g-4" style={{ color: '#1e293b' }}>
        <div className="col-md-12">

          {/* Units Section Header & Stat Cards */}
          <div className="card mb-4" style={cardStyle}>
            <div style={sectionHeaderStyle}>
              <div className="d-flex align-items-center">
                <div style={{ width: '8px', height: '24px', backgroundColor: '#4f46e5', borderRadius: '4px', marginRight: '12px' }}></div>
                <h5 className="mb-0 fw-bold" style={{ fontSize: '1.25rem', letterSpacing: '-0.02em' }}>
                  Units Overview <span className="badge bg-light text-primary ms-2 rounded-pill border" style={{ fontWeight: '600' }}>{totalUnits} Total</span>
                </h5>
              </div>
            </div>
            <div className="card-body p-4">
              <div className="row g-3">

                {/* Occupied Units */}
                <div className="col-md-3 col-xxl-3">
                  <div
                    className="card h-100 p-3"
                    onClick={() => handleViewUnits("occupied")}
                    style={{ ...clickableCardStyle, borderLeft: '4px solid #4f46e5', backgroundColor: '#f8fafc' }}
                  >
                    <div className="d-flex align-items-center justify-content-between">
                      <div>
                        <span className="text-uppercase fw-semibold" style={{ fontSize: '0.75rem', letterSpacing: '0.05em', color: '#64748b' }}>Tenants</span>
                        <h2 className="mb-0 fw-bold mt-1" style={{ color: '#0f172a' }}>{occupiedUnits}</h2>
                        <p className="text-muted mb-0 mt-1" style={{ fontSize: '0.85rem' }}>Occupied By Tenants</p>
                      </div>
                      <div style={iconCircleStyle('#eef2ff')}>
                        <i className="ti ti-lock text-primary"></i>
                      </div>
                    </div>
                  </div>
                </div>

                {/* HomeOwner Units */}
                <div className="col-md-3 col-xxl-3">
                  <div
                    className="card h-100 p-3"
                    onClick={() => handleViewUnits("occupiedByHomeowner")}
                    style={{ ...clickableCardStyle, borderLeft: '4px solid #f59e0b', backgroundColor: '#f8fafc' }}
                  >
                    <div className="d-flex align-items-center justify-content-between">
                      <div>
                        <span className="text-uppercase fw-semibold" style={{ fontSize: '0.75rem', letterSpacing: '0.05em', color: '#64748b' }}>HomeOwners</span>
                        <h2 className="mb-0 fw-bold mt-1" style={{ color: '#0f172a' }}>{occupiedByHomeowner}</h2>
                        <p className="text-muted mb-0 mt-1" style={{ fontSize: '0.85rem' }}>Occupied By HomeOwners</p>
                      </div>
                      <div style={iconCircleStyle('#fef3c7')}>
                        <i className="ti ti-lock text-warning"></i>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Vacant Units */}
                <div className="col-md-3 col-xxl-3">
                  <div
                    className="card h-100 p-3"
                    onClick={() => handleViewUnits("homeOwnerUnitsWithNoTenant")}
                    style={{ ...clickableCardStyle, borderLeft: '4px solid #f59e0b', backgroundColor: '#f8fafc' }}
                  >
                    <div className="d-flex align-items-center justify-content-between">
                      <div>
                        <span className="text-uppercase fw-semibold" style={{ fontSize: '0.75rem', letterSpacing: '0.05em', color: '#64748b' }}>Vacant</span>
                        <h2 className="mb-0 fw-bold mt-1" style={{ color: '#0f172a' }}>{homeOwnerUnitsWithNoTenant}</h2>
                        <p className="text-muted mb-0 mt-1" style={{ fontSize: '0.85rem' }}>Vacant Units</p>
                      </div>
                      <div style={iconCircleStyle('#fef3c7')}>
                        <i className="ti ti-lock text-warning"></i>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Unhanded Over Units */}
                <div className="col-md-3 col-xxl-3">
                  <div
                    className="card h-100 p-3"
                    onClick={() => handleViewUnits("unoccupied")}
                    style={{ ...clickableCardStyle, borderLeft: '4px solid #ef4444', backgroundColor: '#f8fafc' }}
                  >
                    <div className="d-flex align-items-center justify-content-between">
                      <div>
                        <span className="text-uppercase fw-semibold" style={{ fontSize: '0.75rem', letterSpacing: '0.05em', color: '#64748b' }}>Pending</span>
                        <h2 className="mb-0 fw-bold mt-1" style={{ color: '#0f172a' }}>{notOccupiedUnits}</h2>
                        <p className="text-muted mb-0 mt-1" style={{ fontSize: '0.85rem' }}>Unhanded Over Units</p>
                      </div>
                      <div style={iconCircleStyle('#fee2e2')}>
                        <i className="ti ti-lock-open text-danger"></i>
                      </div>
                    </div>
                  </div>
                </div>

              </div>
            </div>
          </div>

          {/* Date Filter Section */}
          {modules.lease && modules.levy && (
            <div className="card mb-4" style={cardStyle}>
              <div style={sectionHeaderStyle}>
                <h5 className="mb-0 fw-bold d-flex align-items-center" style={{ fontSize: '1.1rem' }}>
                  <i className="ti ti-filter text-primary me-2 fs-5"></i>
                  Income Filters
                </h5>
              </div>
              <div className="card-body p-4" style={{ backgroundColor: '#fafafa' }}>
                <div className="row g-3 align-items-end">
                  <div className="col-md-3">
                    <label htmlFor="filterYear" className="form-label fw-semibold text-secondary" style={{ fontSize: '0.85rem' }}>Year</label>
                    <select
                      id="filterYear"
                      className="form-select border-0 shadow-sm"
                      style={{ borderRadius: '10px', padding: '0.6rem 1rem' }}
                      value={selectedYear}
                      onChange={(e) => {
                        const year = e.target.value;
                        setSelectedYear(year);
                        handleMonthYearChange(year, selectedMonth);
                      }}
                    >
                      <option value="">Select Year</option>
                      {years.map(year => (
                        <option key={year} value={year}>{year}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-3">
                    <label htmlFor="filterMonth" className="form-label fw-semibold text-secondary" style={{ fontSize: '0.85rem' }}>Month</label>
                    <select
                      id="filterMonth"
                      className="form-select border-0 shadow-sm"
                      style={{ borderRadius: '10px', padding: '0.6rem 1rem' }}
                      value={selectedMonth}
                      onChange={(e) => {
                        const month = e.target.value;
                        setSelectedMonth(month);
                        handleMonthYearChange(selectedYear, month);
                      }}
                    >
                      <option value="">Select Month</option>
                      {months.map(month => (
                        <option key={month.value} value={month.value}>{month.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-3">
                    <label htmlFor="filterStartDate" className="form-label fw-semibold text-secondary" style={{ fontSize: '0.85rem' }}>Start Date</label>
                    <input
                      type="date"
                      id="filterStartDate"
                      className="form-control border-0 shadow-sm"
                      style={{ borderRadius: '10px', padding: '0.6rem 1rem' }}
                      value={filterStartDate}
                      onChange={(e) => setFilterStartDate(e.target.value)}
                    />
                  </div>
                  <div className="col-md-3">
                    <label htmlFor="filterEndDate" className="form-label fw-semibold text-secondary" style={{ fontSize: '0.85rem' }}>End Date</label>
                    <input
                      type="date"
                      id="filterEndDate"
                      className="form-control border-0 shadow-sm"
                      style={{ borderRadius: '10px', padding: '0.6rem 1rem' }}
                      value={filterEndDate}
                      onChange={(e) => setFilterEndDate(e.target.value)}
                    />
                  </div>
                  <div className="col-md-12 text-end mt-3">
                    <button
                      type="button"
                      className="btn btn-outline-secondary px-4 py-2 border-0 shadow-sm"
                      style={{ borderRadius: '10px', backgroundColor: '#ffffff', fontWeight: '500' }}
                      onClick={clearFilters}
                    >
                      <i className="ti ti-x me-1"></i>
                      Clear Filters
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Summary Cards - Separate Lease and Levy */}

          {modules.lease && (
            <>
              <div className="mb-4">
                {/* Section Header */}
                <div className="d-flex align-items-center justify-content-between mb-3">
                  <div>
                    <div className="d-flex align-items-center">
                      <div
                        className="bg-primary-subtle rounded-3 d-flex align-items-center justify-content-center me-2"
                        style={{ width: '36px', height: '36px' }}
                      >
                        <i className="ti ti-receipt text-primary fs-5"></i>
                      </div>

                      <div>
                        <h6 className="mb-0 fw-bold text-dark">Lease Income</h6>
                        <small className="text-muted">
                          Overview of lease billing and collections
                        </small>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Summary Cards */}
                <div className="row g-3">

                  {/* Total Billed */}
                  <div className="col-md-4">
                    <div
                      className="card border-0 shadow-sm h-100"
                      style={{
                        borderRadius: '12px',
                        background: 'linear-gradient(135deg, #ffffff 0%, #f8fbff 100%)'
                      }}
                    >
                      <div className="card-body p-4">
                        <div className="d-flex align-items-start justify-content-between">

                          <div>
                            <div className="text-muted text-uppercase fw-semibold mb-2"
                              style={{ fontSize: '0.7rem', letterSpacing: '0.06em' }}
                            >
                              Lease Total Billed
                            </div>

                            <h3 className="fw-bold text-primary mb-1">
                              {formatCurrency(leaseData.totalAmount)}
                            </h3>

                            <div className="d-flex align-items-center mt-2">
                              <span className="badge bg-primary-subtle text-primary rounded-pill px-2 py-1">
                                <i className="ti ti-file-invoice me-1"></i>
                                {leaseData.totalCount}
                              </span>

                              <span className="text-muted ms-2">
                                Total Invoices
                              </span>
                            </div>
                          </div>

                          <div
                            className="bg-primary-subtle rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                            style={{ width: '48px', height: '48px' }}
                          >
                            <i className="ti ti-file-invoice text-primary fs-4"></i>
                          </div>

                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Collected */}
                  <div className="col-md-4">
                    <div
                      className="card border-0 shadow-sm h-100"
                      style={{
                        borderRadius: '12px',
                        background: 'linear-gradient(135deg, #ffffff 0%, #f7fdf9 100%)'
                      }}
                    >
                      <div className="card-body p-4">
                        <div className="d-flex align-items-start justify-content-between">

                          <div>
                            <div
                              className="text-muted text-uppercase fw-semibold mb-2"
                              style={{ fontSize: '0.7rem', letterSpacing: '0.06em' }}
                            >
                              Lease Collected
                            </div>

                            <h3 className="fw-bold text-success mb-1">
                              {formatCurrency(leaseData.totalPaid)}
                            </h3>

                            <div className="d-flex align-items-center mt-2">
                              <span className="badge bg-success-subtle text-success rounded-pill px-2 py-1">
                                <i className="ti ti-check me-1"></i>
                                {leaseData.paidCount}
                              </span>

                              <span className="text-muted ms-2">
                                Paid Invoices
                              </span>
                            </div>
                          </div>

                          <div
                            className="bg-success-subtle rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                            style={{ width: '48px', height: '48px' }}
                          >
                            <i className="ti ti-receipt text-success fs-4"></i>
                          </div>

                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Outstanding */}
                  <div className="col-md-4">
                    <div
                      className="card border-0 shadow-sm h-100"
                      style={{
                        borderRadius: '12px',
                        background: 'linear-gradient(135deg, #ffffff 0%, #fff8f8 100%)'
                      }}
                    >
                      <div className="card-body p-4">
                        <div className="d-flex align-items-start justify-content-between">

                          <div>
                            <div
                              className="text-muted text-uppercase fw-semibold mb-2"
                              style={{ fontSize: '0.7rem', letterSpacing: '0.06em' }}
                            >
                              Lease Outstanding
                            </div>

                            <h3 className="fw-bold text-danger mb-1">
                              {formatCurrency(leaseData.totalBalance)}
                            </h3>

                            <div className="d-flex align-items-center mt-2">
                              <span className="badge bg-danger-subtle text-danger rounded-pill px-2 py-1">
                                <i className="ti ti-alert-triangle me-1"></i>
                                {leaseData.unpaidCount}
                              </span>

                              <span className="text-muted ms-2">
                                Unpaid Invoices
                              </span>
                            </div>
                          </div>

                          <div
                            className="bg-danger-subtle rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                            style={{ width: '48px', height: '48px' }}
                          >
                            <i className="ti ti-alert-triangle text-danger fs-4"></i>
                          </div>

                        </div>
                      </div>
                    </div>
                  </div>

                </div>
              </div>
            </>
          )}

          {/* Summary Cards - Levy Section */}
          {modules.levy && (
            <>
              <div className="mb-4">
                {/* Section Header */}
                <div className="d-flex align-items-center justify-content-between mb-3">
                  <div>
                    <div className="d-flex align-items-center">
                      <div
                        className="bg-success-subtle rounded-3 d-flex align-items-center justify-content-center me-2"
                        style={{ width: '36px', height: '36px' }}
                      >
                        <i className="ti ti-coins text-success fs-5"></i>
                      </div>

                      <div>
                        <h6 className="mb-0 fw-bold text-dark">Levy Income</h6>
                        <small className="text-muted">
                          Overview of levy billing and collections
                        </small>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Summary Cards */}
                <div className="row g-3">

                  {/* Total Billed */}
                  <div className="col-md-4">
                    <div
                      className="card border-0 shadow-sm h-100"
                      style={{
                        borderRadius: '12px',
                        background: 'linear-gradient(135deg, #ffffff 0%, #f8fbff 100%)'
                      }}
                    >
                      <div className="card-body p-4">
                        <div className="d-flex align-items-start justify-content-between">

                          <div>
                            <div
                              className="text-muted text-uppercase fw-semibold mb-2"
                              style={{ fontSize: '0.7rem', letterSpacing: '0.06em' }}
                            >
                              Levy Total Billed
                            </div>

                            <h3 className="fw-bold text-primary mb-1">
                              {formatCurrency(levyData.totalAmount)}
                            </h3>

                            <div className="d-flex align-items-center mt-2">
                              <span className="badge bg-primary-subtle text-primary rounded-pill px-2 py-1">
                                <i className="ti ti-file-invoice me-1"></i>
                                {levyData.totalCount}
                              </span>

                              <span className="text-muted ms-2">
                                total invoices
                              </span>
                            </div>
                          </div>

                          <div
                            className="bg-primary-subtle rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                            style={{ width: '48px', height: '48px' }}
                          >
                            <i className="ti ti-file-invoice text-primary fs-4"></i>
                          </div>

                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Collected */}
                  <div className="col-md-4">
                    <div
                      className="card border-0 shadow-sm h-100"
                      style={{
                        borderRadius: '12px',
                        background: 'linear-gradient(135deg, #ffffff 0%, #f7fdf9 100%)'
                      }}
                    >
                      <div className="card-body p-4">
                        <div className="d-flex align-items-start justify-content-between">

                          <div>
                            <div
                              className="text-muted text-uppercase fw-semibold mb-2"
                              style={{ fontSize: '0.7rem', letterSpacing: '0.06em' }}
                            >
                              Levy Collected
                            </div>

                            <h3 className="fw-bold text-success mb-1">
                              {formatCurrency(levyData.totalPaid)}
                            </h3>

                            <div className="d-flex align-items-center mt-2">
                              <span className="badge bg-success-subtle text-success rounded-pill px-2 py-1">
                                <i className="ti ti-check me-1"></i>
                                {levyData.paidCount}
                              </span>

                              <span className="text-muted ms-2">
                                Paid Invoices
                              </span>
                            </div>
                          </div>

                          <div
                            className="bg-success-subtle rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                            style={{ width: '48px', height: '48px' }}
                          >
                            <i className="ti ti-coin text-success fs-4"></i>
                          </div>

                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Outstanding */}
                  <div className="col-md-4">
                    <div
                      className="card border-0 shadow-sm h-100"
                      style={{
                        borderRadius: '12px',
                        background: 'linear-gradient(135deg, #ffffff 0%, #fff8f8 100%)'
                      }}
                    >
                      <div className="card-body p-4">
                        <div className="d-flex align-items-start justify-content-between">

                          <div>
                            <div
                              className="text-muted text-uppercase fw-semibold mb-2"
                              style={{ fontSize: '0.7rem', letterSpacing: '0.06em' }}
                            >
                              Levy Outstanding
                            </div>

                            <h3 className="fw-bold text-danger mb-1">
                              {formatCurrency(levyData.totalBalance)}
                            </h3>

                            <div className="d-flex align-items-center mt-2">
                              <span className="badge bg-danger-subtle text-danger rounded-pill px-2 py-1">
                                <i className="ti ti-alert-triangle me-1"></i>
                                {levyData.unpaidCount}
                              </span>

                              <span className="text-muted ms-2">
                                Unpaid Invoices
                              </span>
                            </div>
                          </div>

                          <div
                            className="bg-danger-subtle rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                            style={{ width: '48px', height: '48px' }}
                          >
                            <i className="ti ti-alert-triangle text-danger fs-4"></i>
                          </div>

                        </div>
                      </div>
                    </div>
                  </div>

                </div>
              </div>
            </>
          )}

          {/* Combined Overview Cards */}
          {modules.lease && modules.levy && (
            <>
              <div className="mb-4">
                {/* Section Header */}
                <div className="d-flex align-items-center justify-content-between mb-3">
                  <div>
                    <div className="d-flex align-items-center">
                      <div
                        className="bg-secondary-subtle rounded-3 d-flex align-items-center justify-content-center me-2"
                        style={{ width: '36px', height: '36px' }}
                      >
                        <i className="ti ti-chart-pie text-secondary fs-5"></i>
                      </div>

                      <div>
                        <h6 className="mb-0 fw-bold text-dark">Combined Overview</h6>
                        <small className="text-muted">
                          Combined lease and levy financial summary
                        </small>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Summary Cards */}
                <div className="row g-3">

                  {/* Total Billed */}
                  <div className="col-md-4">
                    <div
                      className="card border-0 shadow-sm h-100"
                      style={{
                        borderRadius: '12px',
                        background: 'linear-gradient(135deg, #ffffff 0%, #f8fbff 100%)'
                      }}
                    >
                      <div className="card-body p-4">
                        <div className="d-flex align-items-start justify-content-between">

                          <div>
                            <div
                              className="text-muted text-uppercase fw-semibold mb-2"
                              style={{ fontSize: '0.7rem', letterSpacing: '0.06em' }}
                            >
                              Total Billed
                            </div>

                            <h3 className="fw-bold text-primary mb-1">
                              {formatCurrency(
                                leaseData.totalAmount + levyData.totalAmount
                              )}
                            </h3>

                            <div className="d-flex align-items-center mt-2">
                              <span className="badge bg-primary-subtle text-primary rounded-pill px-2 py-1">
                                <i className="ti ti-file-invoice me-1"></i>
                                {leaseData.totalCount + levyData.totalCount}
                              </span>

                              <span className="text-muted ms-2">
                                Total Invoices
                              </span>
                            </div>
                          </div>

                          <div
                            className="bg-primary-subtle rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                            style={{ width: '48px', height: '48px' }}
                          >
                            <i className="ti ti-chart-bar text-primary fs-4"></i>
                          </div>

                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Total Collected */}
                  <div className="col-md-4">
                    <div
                      className="card border-0 shadow-sm h-100"
                      style={{
                        borderRadius: '12px',
                        background: 'linear-gradient(135deg, #ffffff 0%, #f7fdf9 100%)'
                      }}
                    >
                      <div className="card-body p-4">
                        <div className="d-flex align-items-start justify-content-between">

                          <div>
                            <div
                              className="text-muted text-uppercase fw-semibold mb-2"
                              style={{ fontSize: '0.7rem', letterSpacing: '0.06em' }}
                            >
                              Total Collected
                            </div>

                            <h3 className="fw-bold text-success mb-1">
                              {formatCurrency(
                                leaseData.totalPaid + levyData.totalPaid
                              )}
                            </h3>

                            <div className="d-flex align-items-center mt-2">
                              <span className="badge bg-success-subtle text-success rounded-pill px-2 py-1">
                                <i className="ti ti-check me-1"></i>
                                {leaseData.paidCount + levyData.paidCount}
                              </span>

                              <span className="text-muted ms-2">
                                Paid Invoices
                              </span>
                            </div>
                          </div>

                          <div
                            className="bg-success-subtle rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                            style={{ width: '48px', height: '48px' }}
                          >
                            <i className="ti ti-cash text-success fs-4"></i>
                          </div>

                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Total Outstanding */}
                  <div className="col-md-4">
                    <div
                      className="card border-0 shadow-sm h-100"
                      style={{
                        borderRadius: '12px',
                        background: 'linear-gradient(135deg, #ffffff 0%, #fff8f8 100%)'
                      }}
                    >
                      <div className="card-body p-4">
                        <div className="d-flex align-items-start justify-content-between">

                          <div>
                            <div
                              className="text-muted text-uppercase fw-semibold mb-2"
                              style={{ fontSize: '0.7rem', letterSpacing: '0.06em' }}
                            >
                              Total Outstanding
                            </div>

                            <h3 className="fw-bold text-danger mb-1">
                              {formatCurrency(
                                leaseData.totalBalance + levyData.totalBalance
                              )}
                            </h3>

                            <div className="d-flex align-items-center mt-2">
                              <span className="badge bg-danger-subtle text-danger rounded-pill px-2 py-1">
                                <i className="ti ti-alert-triangle me-1"></i>
                                {leaseData.unpaidCount + levyData.unpaidCount}
                              </span>

                              <span className="text-muted ms-2">
                                Unpaid Invoices
                              </span>
                            </div>
                          </div>

                          <div
                            className="bg-danger-subtle rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                            style={{ width: '48px', height: '48px' }}
                          >
                            <i className="ti ti-alert-octagon text-danger fs-4"></i>
                          </div>

                        </div>
                      </div>
                    </div>
                  </div>

                </div>
              </div>
            </>
          )}

          {/* Charts Row */}
          <div className="row g-4 mb-4">
            {/* Lease Income Chart */}
            {modules.lease && (
              <div className="col-md-6">
                <div className="card h-100" style={cardStyle}>
                  <div style={sectionHeaderStyle}>
                    <h5 className="mb-0 fw-bold d-flex align-items-center" style={{ fontSize: '1.1rem' }}>
                      <i className="ti ti-receipt text-primary me-2 fs-5"></i>
                      Lease Income Analysis
                    </h5>
                    <span className="badge bg-primary-subtle text-primary rounded-pill px-3 py-2" style={{ fontWeight: '600' }}>
                      {leaseData.totalCount} Invoices
                    </span>
                  </div>
                  <div className="card-body p-4">
                    {loading ? (
                      <div className="text-center py-5">
                        <div className="spinner-border text-primary" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="p-3 mb-4 rounded-3" style={{ backgroundColor: '#f8fafc', border: '1px solid #f1f5f9' }}>
                          <div className="row text-center">
                            <div className="col-4 border-end">
                              <span className="text-muted d-block mb-1" style={{ fontSize: '0.8rem' }}>Total Billed</span>
                              <h6 className="mb-0 fw-bold text-primary">{formatCurrency(leaseData.totalAmount)}</h6>
                            </div>
                            <div className="col-4 border-end">
                              <span className="text-muted d-block mb-1" style={{ fontSize: '0.8rem' }}>Collected</span>
                              <h6 className="mb-0 fw-bold text-success">{formatCurrency(leaseData.totalPaid)}</h6>
                            </div>
                            <div className="col-4">
                              <span className="text-muted d-block mb-1" style={{ fontSize: '0.8rem' }}>Outstanding</span>
                              <h6 className="mb-0 fw-bold text-danger">{formatCurrency(leaseData.totalBalance)}</h6>
                            </div>
                          </div>
                        </div>

                        {leaseData.totalCount > 0 ? (
                          <ResponsiveContainer width="100%" height={260}>
                            <PieChart>
                              <Pie
                                data={leaseChartData}
                                cx="50%"
                                cy="50%"
                                innerRadius={65}
                                outerRadius={85}
                                paddingAngle={4}
                                dataKey="amount"
                                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(1)}%`}
                              >
                                <Cell fill={COLORS.primary} />
                                <Cell fill={COLORS.success} />
                                <Cell fill={COLORS.danger} />
                              </Pie>
                              <Tooltip formatter={(value) => formatCurrency(value)} />
                              <Legend verticalAlign="bottom" height={36} />
                            </PieChart>
                          </ResponsiveContainer>
                        ) : (
                          <div className="text-center py-5">
                            <i className="ti ti-inbox text-muted" style={{ fontSize: '48px', opacity: '0.5' }}></i>
                            <p className="text-muted mt-2 fw-medium">No lease invoices found</p>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Levy Income Chart */}
            {modules.levy && (
              <div className="col-md-6">
                <div className="card h-100" style={cardStyle}>
                  <div style={sectionHeaderStyle}>
                    <h5 className="mb-0 fw-bold d-flex align-items-center" style={{ fontSize: '1.1rem' }}>
                      <i className="ti ti-coins text-success me-2 fs-5"></i>
                      Levy Income Analysis
                    </h5>
                    <span className="badge bg-success-subtle text-success rounded-pill px-3 py-2" style={{ fontWeight: '600' }}>
                      {levyData.totalCount} Invoices
                    </span>
                  </div>
                  <div className="card-body p-4">
                    {loading ? (
                      <div className="text-center py-5">
                        <div className="spinner-border text-success" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="p-3 mb-4 rounded-3" style={{ backgroundColor: '#f8fafc', border: '1px solid #f1f5f9' }}>
                          <div className="row text-center">
                            <div className="col-4 border-end">
                              <span className="text-muted d-block mb-1" style={{ fontSize: '0.8rem' }}>Total Billed</span>
                              <h6 className="mb-0 fw-bold text-primary">{formatCurrency(levyData.totalAmount)}</h6>
                            </div>
                            <div className="col-4 border-end">
                              <span className="text-muted d-block mb-1" style={{ fontSize: '0.8rem' }}>Collected</span>
                              <h6 className="mb-0 fw-bold text-success">{formatCurrency(levyData.totalPaid)}</h6>
                            </div>
                            <div className="col-4">
                              <span className="text-muted d-block mb-1" style={{ fontSize: '0.8rem' }}>Outstanding</span>
                              <h6 className="mb-0 fw-bold text-danger">{formatCurrency(levyData.totalBalance)}</h6>
                            </div>
                          </div>
                        </div>

                        {levyData.totalCount > 0 ? (
                          <ResponsiveContainer width="100%" height={260}>
                            <PieChart>
                              <Pie
                                data={levyChartData}
                                cx="50%"
                                cy="50%"
                                innerRadius={65}
                                outerRadius={85}
                                paddingAngle={4}
                                dataKey="amount"
                                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(1)}%`}
                              >
                                <Cell fill={COLORS.primary} />
                                <Cell fill={COLORS.success} />
                                <Cell fill={COLORS.danger} />
                              </Pie>
                              <Tooltip formatter={(value) => formatCurrency(value)} />
                              <Legend verticalAlign="bottom" height={36} />
                            </PieChart>
                          </ResponsiveContainer>
                        ) : (
                          <div className="text-center py-5">
                            <i className="ti ti-inbox text-muted" style={{ fontSize: '48px', opacity: '0.5' }}></i>
                            <p className="text-muted mt-2 fw-medium">No levy invoices found</p>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

        </div>
      </div>

      {/* Primereact Dialog for Units Modal */}
      <Dialog
        header={modalTitle}
        visible={unitListModalOpen}
        style={{ width: '80vw', maxWidth: '900px' }}
        modal
        className="p-fluid"
        onHide={() => setUnitListModalOpen(false)}
      >
        <div className="container py-3">
          {unitList.map((unit) => (
            <div key={unit._id} className="col-12">
              <div className="card shadow-sm p-3 hover-shadow">
                <div className="d-flex justify-content-between align-items-center">
                  <h5
                    className="card-title mb-0 text-primary"
                    style={{ cursor: "pointer", textDecoration: "none" }}
                    onClick={() => {
                      setUnitListModalOpen(false);
                      navigate(`/facility/unit_management/view_unit/${unit._id}`);
                    }}
                  >
                    {unit.name}
                  </h5>
                  <span className="badge bg-secondary">{unit.unitType}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </Dialog>

    </Layout>
  );
}

export default Dashboard;